import React, { useState, useEffect } from "react";
import { Upload, message, Modal } from "antd";
import type { UploadProps } from "antd";
import { Storage } from "aws-amplify";
import styles from "./InternalApproval.module.scss";
import UploadIcon from "../../../../assets/images/upload-icon.svg";
import pdfIcon from "../../../../assets/images/view_pdf.svg";
import jpgIcon from "../../../../assets/images/jpeg-image-icon.svg";
import pngIcon from "../../../../assets/images/png-image-icon.svg";
import excelIcon from "../../../../assets/images/excel-file-icon.svg";
import csvIcon from "../../../../assets/images/csvIcon.svg";
import docIcon from "../../../../assets/images/docIcon.svg";
import { fileUploadProps } from "./constants";
import { FileTypes } from "../../../../enums/FileUploadEnums";
import { UploadMessages } from "../../../../enums/UploadDraggerMessagesEnums";
import { getUploadFileTypeHint } from "../../../../utils/textFormatUtils";
import { mimeTypeLabels } from "../../../../constants/MiscKeys";
import clsx from "clsx";

const iconMapping = {
  [FileTypes.PDF]: pdfIcon,
  [FileTypes.CSV]: csvIcon,
  [FileTypes.XLS]: excelIcon,
  [FileTypes.XLSX]: excelIcon,
  [FileTypes.DOC]: docIcon,
  [FileTypes.DOCX]: docIcon,
  [FileTypes.PNG]: pngIcon,
  [FileTypes.JPEG]: jpgIcon,
  [FileTypes.JPG]: jpgIcon,
};

const InternalApprovalUploadDragger = ({
  handleFileUpload,
  fileType,
  listOfUploadedFiles,
  maxFileSize,
  allowedFileTypes,
  handleDeleteDocument,
  setLastUploadedFile,
  cashOrCreditUpload = false,
}) => {
  const { Dragger } = Upload;
  const {
    maxFileCount,
    defaultSheetStatus,
    fileUploadSuccessMessage,
    uploadingMessage,
    fileSizeWarning,
  } = fileUploadProps;
  const [fileList, setFileList] = useState(listOfUploadedFiles || []);
  const handleUpload = async (
    file,
    currentFileList,
    onSuccess,
    onError,
    onProgress
  ) => {
    try {
      let percent = 0;
      const interval = setInterval(() => {
        percent += 10;
        onProgress({ percent });
        if (percent >= 100) clearInterval(interval);
      }, 100);
      const timestampMs = new Date().getTime();
      const fileName = `${timestampMs}/${file.name}`;
      const hide = message.loading(uploadingMessage, 0);
      const result = await Storage.put(fileName, file, {
        contentType: file.type,
      });
      const fileURL = await Storage.get(result.key, { level: "public" });

      const newFile = {
        name: file.name,
        docAttachment: fileURL?.split("?")[0],
        docAttachmentId: file.uid,
        fileType: file.type,
        sheetStatus: defaultSheetStatus,
        docType: fileType,
        document: fileType,
        signatureVerified: false,
        status: "done",
      };
      hide?.()
      message.success(`${file.name} uploaded successfully`);
      onSuccess(fileUploadSuccessMessage);
      const updatedList = [...currentFileList, newFile];
      setFileList(updatedList);
      handleFileUpload(updatedList);
      setLastUploadedFile((prevFiles) => [
        ...(Array.isArray(prevFiles) ? prevFiles : []),
        newFile,
      ]);
    } catch (error) {
      console.error("Upload failed:", error);
      message.error(`${file.name} upload failed`);
      onError(error);
    }
  };
  const maxCount = maxFileSize || maxFileCount;
  const props: UploadProps = {
    fileList,
    maxCount: maxCount,
    headers: {
      authorization: "authorization-text",
    },
    beforeUpload: (file) => {
      const isDuplicate = fileList.some(
        (existing) => existing.name === file.name
      );
      if (fileList.length >= maxCount && !isDuplicate) {
        message.error(`You can only upload up to ${maxCount} files.`);
        return Upload.LIST_IGNORE;
      }
      if (isDuplicate) {
        Modal.confirm({
          title: "Duplicate File",
          content: `${file?.name} is already uploaded. Do you want to replace it?`,
          onOk: () => {
            const fileToReplace = fileList?.find(
              (item) => item?.name === file?.name
            );
            const updatedFileList = fileList?.filter(
              (item) => item?.name !== file?.name
            );
            if (fileToReplace?.id) {
              handleDeleteDocument(fileToReplace?.id);
            }

            if (fileToReplace) {
              setLastUploadedFile((prevFiles) => {
                if (!Array.isArray(prevFiles)) return null;
                return prevFiles?.filter(
                  (item) =>
                    item?.docAttachmentId !== fileToReplace?.docAttachmentId
                );
              });
            }

            setFileList(updatedFileList);
            handleFileUpload(updatedFileList);

            props.customRequest?.({
              file,
              updatedFileList,
              onSuccess: () => {},
              onError: () => {},
              onProgress: () => {},
            });
          },
        });

        return Upload.LIST_IGNORE;
      }

      if (allowedFileTypes && allowedFileTypes.length > 0) {
        if (!allowedFileTypes.includes(file.type)) {
          const readableTypes = allowedFileTypes
            .map((t) => mimeTypeLabels[t] || "files")
            .join(", ");
          message.error(
            `Invalid file type. Only ${readableTypes} are allowed.`
          );
          return Upload.LIST_IGNORE;
        }
      } else if (!allowedFileTypes && file.type !== FileTypes.PDF) {
        message.error(UploadMessages.ONLY_PDF_FILES);
        return Upload.LIST_IGNORE;
      }

      if (file.size > 10 * 1024 * 1024) {
        message.error(UploadMessages.LESS_THAN_10_MB);
        return Upload.LIST_IGNORE;
      }

      return true;
    },
    customRequest: ({
      file,
      updatedFileList,
      onSuccess,
      onError,
      onProgress,
    }) => {
      handleUpload(
        file,
        updatedFileList ?? fileList,
        onSuccess,
        onError,
        onProgress
      );
    },
    onRemove: async (file) => {
      const updatedFileList = fileList.filter((item) => item.uid !== file.uid);
      if (file?.id && !!!cashOrCreditUpload) {
        await handleDeleteDocument(file?.id);
      }
      setFileList(updatedFileList);
      handleFileUpload(updatedFileList);
      setLastUploadedFile((prevFiles) => {
        if (!Array.isArray(prevFiles)) return null;
        return prevFiles?.filter((item) => item?.docAttachmentId !== file?.uid);
      });
    },
    iconRender: (file) => {
      const iconSrc =
        iconMapping[file?.type] ?? (iconMapping[file?.fileType] || pdfIcon);
      return (
        <img
          src={iconSrc}
          alt={file.name}
          style={{ width: 35, height: 35, marginRight: 8, marginTop: 8 }}
        />
      );
    },
  };

  useEffect(() => {
    setFileList(listOfUploadedFiles);
  }, [listOfUploadedFiles]);

  return (
    <div className={clsx(styles.draggerContainer, {
      [styles.cashOrCreditUpload]: cashOrCreditUpload
    })}>
      <Dragger {...props}>
        <p className={styles.UploadIconContainer}>
          <img src={UploadIcon} alt="upload icon" />
        </p>
        <p className="ant-upload-text">
          Click or drag file to this area to upload
        </p>
        <p className="ant-upload-hint">{fileSizeWarning} {getUploadFileTypeHint(allowedFileTypes)}</p>
      </Dragger>
    </div>
  );
};

export default InternalApprovalUploadDragger;
